import moment from 'moment';
import { getUserTimezoneOrFallback } from '../utils/timezone';
import {
    Checkboxes,
    CmsUserHistory,
    NullableTranslationMap,
    TranslationMap,
} from './index';
import { ProvinceResponse } from '../stores/business-unit';

/*
    External Game
*/

// Labatt promotion levels constants
export const LABATT_PROMOTION_MIN_LEVEL = 1;
export const LABATT_PROMOTION_MAX_LEVEL = 4;

export enum WebViewAuthentication {
    NONE = 'none',
    MOBILE_SESSION = 'mobile_session',
    PARTNER_CODE = 'partner_code',
}

export interface ExternalGame {
    id: number;
    status: string;
    startDate: string;
    endDate: string;
    timezone: string;
    type: 'external';
    isFeatured: boolean;
    image: NullableTranslationMap;
    detailImage: NullableTranslationMap;
    backgroundColor: NullableTranslationMap | null;
    titleColor: NullableTranslationMap | null;
    subtitleColor: NullableTranslationMap | null;
    buttonColor: NullableTranslationMap | null;
    title: TranslationMap;
    subTitle: TranslationMap;
    description: TranslationMap;
    externalUrl: TranslationMap;
    phoneNumberValidationRequired: boolean;
    six12RegistrationUrl: string;
    buttonTitle: TranslationMap;
    checkboxes: Checkboxes[];
    ctDeepLink: string | null;
    ckDeepLink: string | null;
    cmsUserHistory: CmsUserHistory[] | null;
    cmsUserEmail: string | null;
    isForLabatt: boolean;
    webviewAuthentication: WebViewAuthentication;
    featureFlagName: string | null;
    labattPromotions: {
        id: number;
        title: string;
        subTitle?: string | null | undefined;
        thumbnail?: string | null | undefined;
        level: number;
    }[];
    provinces: ProvinceResponse[];
    /**
     * The contest this game lists in the applications, which edits it. Absent
     * from an API that predates contests owning their game.
     */
    contestId?: number | null;
}

export type GameFormProvince = {
    businessUnitId: number | null; // null for new selections
    provinceId: number;
};

export type ExternalGamePayload = Omit<
    ExternalGame,
    'labattPromotions' | 'six12RegistrationUrl'
> & {
    six12RegistrationUrl: string | null;
    labattPromotions: {
        id: number;
        level: number;
    }[];
};

// Form type with moments instead of strings for date fields
export type ExternalGameForm = Omit<
    ExternalGame,
    'startDate' | 'endDate' | 'labattPromotions' | 'provinces'
> & {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
    labattPromotions: (
        | {
              id: number;
              title: string;
              subTitle?: string | null | undefined;
              thumbnail?: string | null | undefined;
              level: number;
          }[]
        | null
    )[];
    provinces: GameFormProvince[];
};

export type ExternalGameRequestCms = Omit<
    ExternalGameForm,
    'startDate' | 'endDate' | 'labattPromotions' | 'isFeatured'
> & {
    startDate: string;
    endDate: string;
    labattPromotions: ExternalGame['labattPromotions'];
};

export const initialExternalGameForm: ExternalGameForm = {
    id: 0,
    startDate: moment().startOf('day'),
    endDate: moment().startOf('day'),
    timezone: getUserTimezoneOrFallback(),
    status: 'inactive',
    type: 'external',
    isFeatured: true,
    image: { en: '', fr: '' },
    detailImage: { en: '', fr: '' },
    backgroundColor: null,
    titleColor: null,
    subtitleColor: null,
    buttonColor: null,
    description: { en: '', fr: '' },
    subTitle: { en: '', fr: '' },
    title: { en: '', fr: '' },
    externalUrl: { en: '', fr: '' },
    phoneNumberValidationRequired: false,
    six12RegistrationUrl: '',
    buttonTitle: { en: '', fr: '' },
    checkboxes: [],
    cmsUserHistory: null,
    ctDeepLink: null,
    ckDeepLink: null,
    cmsUserEmail: '',
    isForLabatt: false,
    webviewAuthentication: WebViewAuthentication.NONE,
    featureFlagName: null,
    labattPromotions: Array.from(
        { length: LABATT_PROMOTION_MAX_LEVEL },
        () => null
    ),
    provinces: [] as GameFormProvince[],
    contestId: null,
};

export type GameType = 'external';
export const GameType = ['external'] as const;
